import moment from "moment";

// Mirrors msb-api lib/api_helpers/checkin-insights.js (sc-47706).
export const PROVIDERS = ["claude", "chatgpt"] as const;
export type Provider = (typeof PROVIDERS)[number];

export const PROVIDER_LABELS: Record<Provider, string> = {
  claude: "Claude",
  chatgpt: "ChatGPT",
};

export const PROVIDER_MARKS: Record<Provider, string> = {
  claude: "C",
  chatgpt: "G",
};

const PROVIDER_URLS: Record<Provider, string> = {
  claude: "https://claude.ai/new?q=",
  chatgpt: "https://chatgpt.com/?q=",
};

export const SECTION_KEYS = [
  "summary",
  "trends",
  "winning",
  "goingOff",
  "improve",
  "coachSaying",
  "keyInsights",
  "suggestedChanges",
] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

export const AREA_NAMES = ["training", "nutrition", "daily", "goals"] as const;
export type AreaName = (typeof AREA_NAMES)[number];

export type TrendArea = { name: AreaName; bullets: string[] };
export type SectionContent =
  | { text: string }
  | { areas: TrendArea[] }
  | { bullets: string[] };
export type Section = SectionContent & { edited?: boolean };

export type InsightRequest = {
  id: string;
  provider: Provider;
  status: "pending" | "ready" | "failed" | "expired" | "cancelled";
  createdAt: string;
  expiresAt: string;
  fetchedAt?: string;
  completedAt?: string;
};

export type Insight = {
  provider: Provider;
  model?: string;
  dataCutoff: string;
  generatedAt: string;
  editedAt?: string;
  editedBy?: string;
  sections: Record<SectionKey, Section>;
};

export type AiConnection = { provider: string; connected: boolean };

/** The 2 min "didn't hear back" window runs from the click until the agent's first read (Mike #7). */
export const NO_FETCH_TIMEOUT_MS = 2 * 60 * 1000;

export type InsightsView =
  | "empty"
  | "waiting"
  | "nohear"
  | "regen"
  | "ready"
  | "failed"
  | "expired";

export const insightsView = (
  insight: Insight | null,
  request: InsightRequest | null,
  now = Date.now()
): InsightsView => {
  const status = request?.status;
  if (status === "pending") {
    const waitedMs = now - new Date(request.createdAt).getTime();
    if (!request.fetchedAt && waitedMs > NO_FETCH_TIMEOUT_MS) return "nohear";
    return insight ? "regen" : "waiting";
  }
  if (status === "failed" || status === "expired") return status;
  return insight ? "ready" : "empty";
};

export const connectedProviders = (connections: AiConnection[]): Provider[] =>
  PROVIDERS.filter((provider) =>
    connections.some(
      (connection) => connection.provider === provider && connection.connected
    )
  );

/** Same period and wording as the API's prompt, so the week matches the check-in view. */
export const formatPeriod = (start: string, end: string) => {
  const from = moment(start);
  const to = moment(end);
  const startFormat = from.year() === to.year() ? "MMM D" : "MMM D, YYYY";
  return `${from.format(startFormat)} – ${to.format("MMM D, YYYY")}`;
};

export const providerUrl = (provider: Provider, prompt: string) =>
  PROVIDER_URLS[provider] + encodeURIComponent(prompt);

const BULLET_PREFIX = /^\s*(?:[•\-*]|\d+[.)])\s+/;

/** One bullet per non-empty line; pasted "• " / "- " / "1." markers are dropped. */
export const linesToBullets = (text: string) =>
  text
    .split("\n")
    .map((line) => line.replace(BULLET_PREFIX, "").trim())
    .filter(Boolean);

export const bulletsToLines = (bullets: string[]) => bullets.join("\n");
